import { defineStore } from 'pinia';
import type { Identity, Role } from '@its/shared';
import { api, ApiError } from '@/lib/api';
import { setAuthHeaders } from '@/lib/session';
import { demarrerTempsReel, arreterTempsReel } from '@/lib/realtime';
import { couperPushALaDeconnexion } from '@/lib/push';

const DEV_EMAIL_KEY = 'pa.devEmail';

interface AuthConfig {
  /** SSO Microsoft Entra disponible. */
  sso: boolean;
  /** Connexion de développement par e-mail (jamais en production). */
  devLogin: boolean;
}

interface State {
  user: Identity | null;
  config: AuthConfig;
  ready: boolean;
  loading: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: null,
    config: { sso: false, devLogin: false },
    ready: false,
    loading: false,
  }),
  getters: {
    isAuthenticated: (s): boolean => s.user !== null,
    roles: (s): Role[] => s.user?.roles ?? [],
    isDirection: (s): boolean => (s.user?.roles ?? []).includes('direction'),
    hasRole:
      (s) =>
      (...roles: Role[]): boolean =>
        (s.user?.roles ?? []).some((r) => roles.includes(r)),
  },
  actions: {
    /** Restaure la session : cookie SSO d'abord, puis e-mail mémorisé en dev. */
    async init(): Promise<void> {
      if (this.ready) return;
      try {
        this.config = await api.get<AuthConfig>('/auth/config');
      } catch {
        // API indisponible : on reste déconnecté.
      }

      // Le cookie de session (SSO) part automatiquement avec la requête.
      await this.fetchMe();

      if (!this.user && this.config.devLogin) {
        const email = localStorage.getItem(DEV_EMAIL_KEY);
        if (email) {
          setAuthHeaders({ 'X-Debug-User': email });
          await this.fetchMe();
        }
      }
      this.ready = true;
    },

    async fetchMe(): Promise<void> {
      try {
        const { user } = await api.get<{ user: Identity | null }>('/me');
        this.user = user;
        /*
         * LE FLUX TEMPS RÉEL SUIT L'IDENTITÉ. Il est (r)ouvert ici et nulle part
         * ailleurs : c'est le seul endroit qui sait à la fois QUI l'on est et
         * que les en-têtes d'authentification sont posés. L'ouvrir plus tôt
         * donnerait un 401, plus tard obligerait chaque écran à y penser.
         */
        demarrerTempsReel(user?.userId ?? null);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) this.user = null;
        else throw err;
      }
    },

    /** Redirige vers Microsoft ; le retour repose le cookie de session. */
    loginWithMicrosoft(redirect = '/'): void {
      window.location.href = `/api/auth/login?redirect=${encodeURIComponent(redirect)}`;
    },

    /** Connexion de développement : identification par e-mail (X-Debug-User). */
    async devLogin(email: string): Promise<void> {
      this.loading = true;
      try {
        setAuthHeaders({ 'X-Debug-User': email });
        await this.fetchMe();
        if (this.user) {
          localStorage.setItem(DEV_EMAIL_KEY, email);
          // Enregistre la connexion (borne du « depuis la dernière connexion »).
          try {
            await api.post('/auth/session-start');
          } catch {
            // Non bloquant : la pastille se contentera de la valeur précédente.
          }
        } else throw new ApiError(401, 'Collaborateur inconnu ou inactif');
      } finally {
        this.loading = false;
      }
    },

    async logout(): Promise<void> {
      /*
       * ON COUPE LE PUSH AVANT DE PARTIR, et avant de fermer la session — la
       * route de désabonnement demande une identité.
       *
       * Sans cela, le collègue qui se connecte ensuite sur le même poste
       * verrait s'afficher les notifications du précédent sur son écran de
       * veille : le canal de push appartient au NAVIGATEUR, pas au compte.
       */
      await couperPushALaDeconnexion();
      try {
        await api.post('/auth/logout');
      } catch {
        // Session déjà expirée : on nettoie quand même côté client.
      }
      localStorage.removeItem(DEV_EMAIL_KEY);
      setAuthHeaders({});
      arreterTempsReel();
      this.user = null;
    },
  },
});
